import { DecimalPipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, inject, input, OnInit, output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ProjectObserverService } from '@app/cem/project/services/project-observer.service';
import { ErrorService } from '@app/common/services/error.service';
import { PlexEnvironment } from '@app/env';
import { ButtonLoadingDirective } from '@app/shared/directives/button-loading/button-loading.directive';
import { DisabledControlDirective } from '@app/shared/directives/disabled-control/disabled-control.directive';
import { kendoIcons } from '@app/shared/library/kendo-icons';
import { SafeNumberPipe } from '@app/shared/pipes/safe-number/safe-number.pipe';
import { ButtonDirective } from '@progress/kendo-angular-buttons';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { DropDownListComponent } from '@progress/kendo-angular-dropdowns';
import { SVGIconComponent } from '@progress/kendo-angular-icons';
import { NumericTextBoxComponent } from '@progress/kendo-angular-inputs';
import { FuelType, ProjectStatusId, ProjectTypeId } from 'app/shared/data/project-enums';
import { CompletionsParameter } from '../../models/completions-parameter';
import { LookupPlusService } from '../../services/lookups/lookup-plus.service';
import { ProjectCustomEndpointService } from '../../services/project-custom-endpoint.service';
import { ProjectCompletionFluidComponent } from '../project-completion-fluid/project-completion-fluid.component';
import { ProjectDrillingPhaseStepComponent } from '../project-drilling-phase-step/project-drilling-phase-step.component';

@Component({
  selector: 'app-project-completion-details',
  templateUrl: './project-completion-details.component.html',
  styleUrls: ['./project-completion-details.component.scss'],
  imports: [
    ButtonDirective,
    ButtonLoadingDirective,
    DropDownListComponent,
    DisabledControlDirective,
    FormsModule,
    NumericTextBoxComponent,
    ProjectCompletionFluidComponent,
    ProjectDrillingPhaseStepComponent,
    ReactiveFormsModule,
    SafeNumberPipe,
    SVGIconComponent,
    DialogModule
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    DecimalPipe, //Needed for SafeNumberPipe
  ],
})
export class ProjectCompletionDetailsComponent implements OnInit, AfterViewInit {
  private fb = inject(UntypedFormBuilder);
  private lookupPlusService = inject(LookupPlusService);
  private projectCustomEndpointService = inject(ProjectCustomEndpointService);
  private errorService = inject(ErrorService);
  private poSvc = inject(ProjectObserverService);
  public environment = inject(PlexEnvironment);

  public icons = kendoIcons;

  private tCleanoutMethod: string = 'CleanoutMethods';
  private tDrillOutMethod: string = 'DrilloutMethods';
  private tArtificialLiftType: string = 'ArtificialLiftTypes';
  private tProppantType: string = 'ProppantTypes?$select=ProppantTypeId%2C%20Description';
  private tFluidSystem: string = 'FluidSystems';
  private dFluidSystem: any[] = [];
  public dCleanoutMethod: any[] = [];
  public dDrilloutMethod: any[] = [];
  public dArtificialLiftType: any[] = [];
  public tWaterType: string = 'WaterTypes';
  public dWaterType: any[] = [];
  public tFuelType: string = 'FuelTypes';
  public dFuelType: any[] = [];
  public completionsObject: CompletionsParameter;
  public updateCompletionsObject: CompletionsParameter;
  public dProppantType = Array<any>();
  public drilloutMethod: string;
  public artificialLiftType: string;
  public cleanoutMethod: string;
  public stagingData: any;
  public fluidData: any;
  public proppantType1Description: string;
  public proppantType2Description: string;
  public proppantType3Description: string;
  public proppantType4Description: string;
  public costBasisData: any;
  public fracVendorData: any;
  public fracTypeData: any[] = [];
  public completionForm: UntypedFormGroup;
  public fluidDataForm: UntypedFormGroup;
  public decimalPrecision: number = 0;
  public decimalFormat: string = 'n0';
  public showStimProject: boolean = true;
  public showPostStimProject: boolean = true;
  public loading: boolean = false;
  public parameterTooltipLabel: string = "Please note: percent values will be reset when you refresh parameters.";
  public isTooltipVisible: boolean = false;

  // emitter tabTdv provides touched-dirty-valid status of the form in this tab
  readonly tabTdv = output<boolean>();
  readonly saveComplete = output<boolean>();

  readonly iProjectId = input<number>(undefined);
  readonly iProjectTypeId = input<number>(undefined);
  readonly iProjectStatusId = input<number>(undefined);

  async ngOnInit() {
    this.generateForm();
    await Promise.all([
      this.getFluidSystem(this.tFluidSystem),
      this.getProppantType(this.tProppantType),
      this.getArtificialLiftType(this.tArtificialLiftType),
      this.getDrilloutMethod(this.tDrillOutMethod),
      this.getCleanoutMethod(this.tCleanoutMethod),
      this.getFuelType(this.tFuelType),
      this.getWaterType(this.tWaterType)]
    );

    if (this.iProjectTypeId() === ProjectTypeId.CompleteStimOAG)
      this.showPostStimProject = false;
    if (this.iProjectTypeId() === ProjectTypeId.CompletePostStimOAG)
      this.showStimProject = false;
    if (this.iProjectStatusId() === ProjectStatusId.Closed || this.iProjectStatusId() === ProjectStatusId.Cancelled) {
      this.completionForm.disable();
      this.fluidDataForm.disable();
    } else {
      await this.loadCostBasis();
      await this.loadFracVendor();
    }
    this.getCompletionDetails(false, this.environment.useNewEngine);
  }

  ngAfterViewInit() {
    this.poSvc.projectObserve('parameters', this.completionForm.valid);
    this.poSvc.projectObserve('parameters', this.fluidDataForm.valid);
  }

  private getDrilloutMethod(table: string) {
    return this.lookupPlusService.getLookupNoCount(table).then(payload => {
      this.dDrilloutMethod = payload.value;
    });
  }

  private getArtificialLiftType(table: string) {
    return this.lookupPlusService.getLookupNoCount(table).then(payload => {
      this.dArtificialLiftType = payload.value;
    });
  }

  private getCleanoutMethod(table: string) {
    return this.lookupPlusService.getLookupNoCount(table).then(payload => {
      this.dCleanoutMethod = payload.value;
    });
  }

  private getFluidSystem(table: string) {
    return this.lookupPlusService.getLookupNoCount(table).then(payload => {
      this.dFluidSystem = payload.value;
    });
  }

  private getWaterType(table: string) {
    return this.lookupPlusService.getLookupNoCount(table).then(payload => {
      this.dWaterType = payload.value;
    });
  }

  private getFuelType(table: string) {
    return this.lookupPlusService.getLookupNoCount(table).then(payload => {
      this.dFuelType = payload;
    });
  }

  private getProppantType(table: string) {
    return this.lookupPlusService.getLookupNoCount(table).then(payload => {
      if (payload.value.length > 0) {
        this.dProppantType = payload.value;
      }
    });
  }

  private getCompletionDataItem(key: string): number {
    if (!this.completionsObject || !this.completionsObject.CompletionData) {
      return null;
    }

    let itemValue: number = 0;
    let selected: any = this.completionsObject.CompletionData.find(x => x.Key === key);
    if (selected && selected.Value !== null && selected.Value >= 0) {
      itemValue = selected.Value;
    }
    return itemValue;
  }

  private getFluidSystemById(id: number): string {
    if (this.dFluidSystem.length == 0) {
      throw 'FluidSystems have not been loaded, but are expected.';
    }
    let selected: any = null;
    if (id !== null) {
      let fluidSystem = this.dFluidSystem.find(x => x.FluidSystemId === id);
      selected = fluidSystem.Description;
    }
    return selected;
  }

  private getProppantTypeById(id: number): string {
    let selected: any = null;
    if (id !== null) {
      let proppantType = this.dProppantType.find(x => x.ProppantTypeId === id);
      selected = proppantType.Description;
    }
    return selected;
  }

  private async getCompletionDetails(refresh: boolean, useNewEngine: boolean) {
    this.loading = true;

    let command: string = `estimation/projects/${this.iProjectId()}`;
    let endpoint: string = `completionsparameters?refresh=${refresh}&useNewEngine=${useNewEngine}`;

    try {
      let payload = await this.lookupPlusService.getObject(command, endpoint);

      this.completionsObject = Object.assign({}, payload);
      this.updateCompletionsObject = Object.assign({}, payload);
      await this.setupData();
    }
    catch (error) {
      this.errorService.error('Error Getting Completion Details', error);
    }
    finally {
      this.loading = false;
    }
  }

  private async setupData() {
    // Frac types must be loaded BEFORE populateForm() runs so the Kendo
    // DropDownList ([valuePrimitive]="true") can resolve completionsObject.FracTypeId
    // to a list item and highlight it. Awaiting here removes the race condition
    // between the scenario-scoped frac-types lookup and the initial form assignment.
    await this.loadFracTypes();
    this.proppantType1Description = this.getProppantTypeById(this.completionsObject.ProppantType1ItemId);
    this.proppantType2Description = this.getProppantTypeById(this.completionsObject.ProppantType2ItemId);
    this.proppantType3Description = this.getProppantTypeById(this.completionsObject.ProppantType3ItemId);
    this.proppantType4Description = this.getProppantTypeById(this.completionsObject.ProppantType4ItemId);

    this.stagingData = {
      StageCount: this.completionsObject.StageCount, // this.getCompletionDataItem('StageCount'),
      DrilloutMethod: this.completionsObject.Drilloutmethod,
      StageLength: this.completionsObject.StageLength,
      TreatingPressure: this.getCompletionDataItem('TreatingPressure2'),
      PlugsPerDay: this.getCompletionDataItem('PlugsPerDay'),
      ClustersPerStage: this.completionsObject.ClustersPerStage,
      CompleteLateralLength: this.completionsObject.CompleteLateralLength,
      CompletedLateralLengthStored: this.completionsObject.CompletedLateralLengthStored,
      DrilledLateralLength: this.completionsObject.DrilledLateralLength,
      LateralFeetCompletedPerDay: null,
      NumberOfClusters: null,
      PumpTime: this.completionsObject.PumpTime,
      WellsInFracGroup: this.getCompletionDataItem('WellsInFracGroup'),
      WellsInDrillGroup: this.getCompletionDataItem('WellsInDrillGroup'),
      VerticalSection: this.getCompletionDataItem('VerticalSection'),
      DesignedRate: this.completionsObject.PumpRate
    };

    this.fluidData = {
      FluidSystem: this.getFluidSystemById(this.completionsObject.FluidSystemId),
      CrossLinkPct: this.completionsObject.CrossLinkPct,
      HvfrPct: this.completionsObject.HvfrPct,
      GelWeight: this.completionsObject.GelWeight,
      LinearGelPct: this.completionsObject.LinearGelPct,
      SlickWaterPct: this.completionsObject.SlickWaterPct,
      WaterVolume: this.getCompletionDataItem('FluidQuantity'),
      FracWaterVolume: this.getCompletionDataItem('FracWaterVolume'),
      PumpdownWaterVolume: this.completionsObject.PumpdownWaterVolume
    };

    this.populateForm();
  } 

  private generateForm(): void {
    this.completionForm = this.fb.group({
      Is24HourOperation: [false],
      IsSimulFracFleet: [false],
      IsGateGuarded: [false],
      DFITTest: [false],
      IsRentalESPPump: [false],
      IsProductionBHPGauges: [false],
      CostBasis: [],
      FracVendor: [],
      FracType: [],
      WaterType: [],
      CompletionFuelType: [],
      CompletionFuelTypeOverride: [],
      StageCountOverride: [],
      PumpRateOverride: [],
      ArtificialLiftType: [],
      DrillOutMethod: [{ value: null, disabled: this.showPostStimProject }],
      CleanOutMethod: [],
      LandingPoint: [null],
      LandingPointOverride: [null],
      BottomHoleLocationXCoordinate: [null],
      BottomHoleLocationYCoordinate: [null],
      FirstTakePointXCoordinate: [null],
      FirstTakePointYCoordinate: [null],
      SurfaceHoleLocationPointOfPenetrationXCoordinate: [null],
      SurfaceHoleLocationPointOfPenetrationYCoordinate: [null],
      BottomHoleLocationXCoordinateOverride: [null],
      BottomHoleLocationYCoordinateOverride: [null],
      FirstTakePointXCoordinateOverride: [null],
      FirstTakePointYCoordinateOverride: [null],
      SurfaceHoleLocationPointOfPenetrationXCoordinateOverride: [null],
      SurfaceHoleLocationPointOfPenetrationYCoordinateOverride: [null],
      ProppantType1Percent: [null],
      ProppantType2Percent: [null],
      ProppantType3Percent: [null],
      ProppantType4Percent: [null],
      PumpRate: [null],
      DGBPercent: [null]
    });
    this.fluidDataForm = this.fb.group({
      CrossLinkPct: [null],
      HvfrPct: [null],
      LinearGelPct: [null],
      SlickWaterPct: [null]
    });
    this.completionForm.valueChanges.subscribe(data => this.onValueChanged(data));
    this.fluidDataForm.valueChanges.subscribe(data => this.onValueChanged(data));
  }

  private onValueChanged(data?: any) {
    if (!this.completionForm || !this.fluidDataForm) {
      return;
    }
    this.poSvc.projectObserve('parameters', this.completionForm.valid);
    this.poSvc.projectObserve('parameters', this.fluidDataForm.valid);
    if ((this.completionForm.valid && this.completionForm.dirty) || (this.fluidDataForm.valid && this.fluidDataForm.dirty)) {
      this.setTDV();
    } else {
      this.clearTDV();
    }
  }

  public onFuelTypeChanged(data?: any) {
    if (data == null)
      return;

    if (this.completionForm.controls.CompletionFuelType.value == FuelType.DGB) {
      this.completionForm.controls.DGBPercent.enable();
    }
    else {
      this.completionForm.controls.DGBPercent.setValue(0);
      this.completionForm.controls.DGBPercent.disable();
    }
  }

  private populateForm(): void {
    this.completionForm.reset();
    this.fluidDataForm.reset();
    this.completionForm.setValue({
      DFITTest: this.completionsObject.DFITTest,
      Is24HourOperation: this.completionsObject.Is24HourOperation,
      IsSimulFracFleet: this.completionsObject.IsSimulFracFleet,
      IsGateGuarded: this.completionsObject.IsGateGuarded,
      IsRentalESPPump: this.completionsObject.IsRentalESPPump,
      IsProductionBHPGauges: this.completionsObject.IsProductionBHPGauges,
      CostBasis: this.completionsObject.CostBasis,
      FracVendor: this.completionsObject.FracVendor,
      FracType: this.completionsObject.FracTypeId ?? null,
      WaterType: this.completionsObject.WaterTypeId,
      CompletionFuelType: this.completionsObject.CompletionFuelTypeId,
      CompletionFuelTypeOverride: this.completionsObject.CompletionFuelTypeOverrideId,
      StageCountOverride: this.completionsObject.StageCountOverride,
      PumpRateOverride: this.completionsObject.PumpRateOverride,
      ArtificialLiftType: this.completionsObject.ArtificialLiftTypeId,
      DrillOutMethod: this.completionsObject.DrillOutMethodId,
      CleanOutMethod: this.completionsObject.CleanOutMethodId,
      LandingPoint: this.completionsObject.LandingPoint,
      LandingPointOverride: this.completionsObject.LandingPointOverride,
      BottomHoleLocationXCoordinate: this.completionsObject.BottomHoleLocationXCoordinate,
      BottomHoleLocationYCoordinate: this.completionsObject.BottomHoleLocationYCoordinate,
      FirstTakePointXCoordinate: this.completionsObject.FirstTakePointXCoordinate,
      FirstTakePointYCoordinate: this.completionsObject.FirstTakePointYCoordinate,
      SurfaceHoleLocationPointOfPenetrationXCoordinate: this.completionsObject.SurfaceHoleLocationPointOfPenetrationXCoordinate,
      SurfaceHoleLocationPointOfPenetrationYCoordinate: this.completionsObject.SurfaceHoleLocationPointOfPenetrationYCoordinate,
      BottomHoleLocationXCoordinateOverride: this.completionsObject.BottomHoleLocationXCoordinateOverride,
      BottomHoleLocationYCoordinateOverride: this.completionsObject.BottomHoleLocationYCoordinateOverride,
      FirstTakePointXCoordinateOverride: this.completionsObject.FirstTakePointXCoordinateOverride,
      FirstTakePointYCoordinateOverride: this.completionsObject.FirstTakePointYCoordinateOverride,
      SurfaceHoleLocationPointOfPenetrationXCoordinateOverride: this.completionsObject.SurfaceHoleLocationPointOfPenetrationXCoordinateOverride,
      SurfaceHoleLocationPointOfPenetrationYCoordinateOverride: this.completionsObject.SurfaceHoleLocationPointOfPenetrationYCoordinateOverride,
      ProppantType1Percent: this.completionsObject.ProppantType1Percent,
      ProppantType2Percent: this.completionsObject.ProppantType2Percent,
      ProppantType3Percent: this.completionsObject.ProppantType3Percent,
      ProppantType4Percent: this.completionsObject.ProppantType4Percent,
      PumpRate: this.completionsObject.PumpRate,
      DGBPercent: this.completionsObject.DGBPercent
    });

    this.fluidDataForm.setValue({
      CrossLinkPct: this.completionsObject.CrossLinkPct,
      HvfrPct: this.completionsObject.HvfrPct,
      LinearGelPct: this.completionsObject.LinearGelPct,
      SlickWaterPct: this.completionsObject.SlickWaterPct
    });
    this.onFuelTypeChanged(this.completionsObject.CompletionFuelTypeId);
  }

  private loadCostBasis(): Promise<any> {
    let filter: string = `$filter=Name%20eq%20'CostBasis'`;
    let select: string = `$select=Name%2C%20Value`;
    let url = `${filter}&${select}`;
    return this.lookupPlusService.getLookup('GeneralAttributes', url)
      .then(payload => {
        this.costBasisData = payload.value;
      });
  }

  private loadFracVendor(): Promise<any> {
    let select: string = `$select=FracFleetVendorId%2C%20VendorName`;
    let url = `${select}`;
    return this.lookupPlusService.getLookup('FracFleetVendors', url)
      .then(payload => {
        this.fracVendorData = payload.value;
      });
  }

  private loadFracTypes(): Promise<any> {
    // CEM exposes the full frac types list through this custom endpoint.
    // Descriptions come from CPPE_SPM.dbo.FracTypes (via spm-api) with a
    // server-side enum fallback, so no scenario filtering is applied here.
    // NOTE: apiUrl already ends in '/api/', so the path must NOT repeat 'api/'.
    return this.lookupPlusService.getLookupCustom(`lookup/fractypes`)
      .then(payload => {
        this.fracTypeData = payload.Results ?? [];
      });
  }

  public updateCompletionParameters(e): void {
    this.updateCompletionsObject.Is24HourOperation = this.completionForm.controls.Is24HourOperation.value;
    this.updateCompletionsObject.IsSimulFracFleet = this.completionForm.controls.IsSimulFracFleet.value;
    this.updateCompletionsObject.IsGateGuarded = this.completionForm.controls.IsGateGuarded.value;
    this.updateCompletionsObject.DFITTest = this.completionForm.controls.DFITTest.value;
    this.updateCompletionsObject.IsRentalESPPump = this.completionForm.controls.IsRentalESPPump.value;
    this.updateCompletionsObject.IsProductionBHPGauges = this.completionForm.controls.IsProductionBHPGauges.value;
    this.updateCompletionsObject.CostBasis = this.completionForm.controls.CostBasis.value;
    this.updateCompletionsObject.FracVendor = this.completionForm.controls.FracVendor.value;
    this.updateCompletionsObject.FracTypeId = this.completionForm.controls.FracType.value;
    this.updateCompletionsObject.WaterTypeId = this.completionForm.controls.WaterType.value;
    this.updateCompletionsObject.CompletionFuelTypeOverrideId = this.completionForm.controls.CompletionFuelTypeOverride.value;
    this.updateCompletionsObject.StageCountOverride = this.completionForm.controls.StageCountOverride.value;
    this.updateCompletionsObject.PumpRateOverride = this.completionForm.controls.PumpRateOverride.value;
    this.updateCompletionsObject.ArtificialLiftTypeId = this.completionForm.controls.ArtificialLiftType.value;
    this.updateCompletionsObject.DrillOutMethodId = this.completionForm.controls.DrillOutMethod.value;
    this.updateCompletionsObject.CleanOutMethodId = this.completionForm.controls.CleanOutMethod.value;

    this.updateCompletionsObject.BottomHoleLocationXCoordinate = this.completionForm.controls.BottomHoleLocationXCoordinate.value;
    this.updateCompletionsObject.BottomHoleLocationYCoordinate = this.completionForm.controls.BottomHoleLocationYCoordinate.value;
    this.updateCompletionsObject.FirstTakePointXCoordinate = this.completionForm.controls.FirstTakePointXCoordinate.value;
    this.updateCompletionsObject.FirstTakePointYCoordinate = this.completionForm.controls.FirstTakePointYCoordinate.value;
    this.updateCompletionsObject.SurfaceHoleLocationPointOfPenetrationXCoordinate = this.completionForm.controls.SurfaceHoleLocationPointOfPenetrationXCoordinate.value;
    this.updateCompletionsObject.SurfaceHoleLocationPointOfPenetrationYCoordinate = this.completionForm.controls.SurfaceHoleLocationPointOfPenetrationYCoordinate.value;

    this.updateCompletionsObject.BottomHoleLocationXCoordinateOverride = this.completionForm.controls.BottomHoleLocationXCoordinateOverride.value;
    this.updateCompletionsObject.BottomHoleLocationYCoordinateOverride = this.completionForm.controls.BottomHoleLocationYCoordinateOverride.value;
    this.updateCompletionsObject.FirstTakePointXCoordinateOverride = this.completionForm.controls.FirstTakePointXCoordinateOverride.value;
    this.updateCompletionsObject.FirstTakePointYCoordinateOverride = this.completionForm.controls.FirstTakePointYCoordinateOverride.value;
    this.updateCompletionsObject.SurfaceHoleLocationPointOfPenetrationXCoordinateOverride = this.completionForm.controls.SurfaceHoleLocationPointOfPenetrationXCoordinateOverride.value;
    this.updateCompletionsObject.SurfaceHoleLocationPointOfPenetrationYCoordinateOverride = this.completionForm.controls.SurfaceHoleLocationPointOfPenetrationYCoordinateOverride.value;

    this.updateCompletionsObject.ProppantType1Percent = this.completionForm.controls.ProppantType1Percent.value;
    this.updateCompletionsObject.ProppantType2Percent = this.completionForm.controls.ProppantType2Percent.value;
    this.updateCompletionsObject.ProppantType3Percent = this.completionForm.controls.ProppantType3Percent.value;
    this.updateCompletionsObject.ProppantType4Percent = this.completionForm.controls.ProppantType4Percent.value;
    this.updateCompletionsObject.PumpRate = this.completionForm.controls.PumpRate.value;
    this.updateCompletionsObject.LandingPointOverride = this.completionForm.controls.LandingPointOverride.value;

    this.updateCompletionsObject.CrossLinkPct = this.fluidDataForm.controls.CrossLinkPct.value;
    this.updateCompletionsObject.HvfrPct = this.fluidDataForm.controls.HvfrPct.value;
    this.updateCompletionsObject.LinearGelPct = this.fluidDataForm.controls.LinearGelPct.value;
    this.updateCompletionsObject.SlickWaterPct = this.fluidDataForm.controls.SlickWaterPct.value;
    this.updateCompletionsObject.ProjectId = this.iProjectId();
    this.updateCompletionsObject.DGBPercent = this.completionForm.controls.DGBPercent.value;

    const updateForm = JSON.stringify(this.updateCompletionsObject);
    let url: string = `estimation/projects/processcompletionsparameters`;

    this.projectCustomEndpointService.processCompletions(url, updateForm)
      .then(() => {
        this.getCompletionDetails(false, this.environment.useNewEngine);
        this.saveComplete.emit(true);
      });
  }

  public cancelChanges(e) {
    this.getCompletionDetails(false, this.environment.useNewEngine);
    this.completionForm.reset();
    this.fluidDataForm.reset();
  }

  public refreshParameters(useNewEngine: boolean) {
    this.getCompletionDetails(true, useNewEngine);
    this.completionForm.reset();
    this.fluidDataForm.reset();
  }

  public setTDV(): void {
    let tdvState: boolean = true;
    this.tabTdv.emit(tdvState);
  }

  public clearTDV(): void {
    let tdvState: boolean = false;
    this.tabTdv.emit(tdvState);
  }
  public onClickTooltipIcon() {
    this.isTooltipVisible = true;
  }

}